/** tm/hooks.ts — 상태 관리 훅 */
import { useState, useEffect, useCallback } from 'react';
import type { Company, FP, WM, Task, Consultation, GovProgram, AIPreset, TaskCategory, TaskTemplate } from './types';
import * as api from './api';

export interface TMStore {
  companies: Company[];
  fps: FP[];
  wms: WM[];
  tasks: Task[];
  consultations: Consultation[];
  govPrograms: GovProgram[];
  aiPresets: AIPreset[];
  taskCategories: TaskCategory[];
  taskTemplates: TaskTemplate[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshCompanies: () => Promise<void>;
  /** 업체 한 곳만 다시 받아 바꿔 끼우기(2026-10-26 — 업종 칩 · WM 하나 바꿀 때마다 전체를 다시 받아 2초 늦고 깜빡였다) */
  refreshCompany: (id: string) => Promise<void>;
  /** 업체 저장 → 그 한 곳만 갱신 */
  saveCompany: (id: string, patch: Parameters<typeof api.updateCompany>[1]) => Promise<void>;
  refreshTasks: () => Promise<void>;
  refreshConsultations: () => Promise<void>;
  /** FP 목록만 (2026-10-18 — 약속 등록 때 전체를 다시 받지 않게) */
  refreshFps: () => Promise<void>;
  /** 정부지원사업 목록만 (2026-10-05s — 공고 화면에서 업체 · 업무 · 상담까지 다시 받지 않게) */
  refreshGov: () => Promise<void>;
}

export function useTMStore(): TMStore {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [fps, setFps] = useState<FP[]>([]);
  const [wms, setWms] = useState<WM[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [govPrograms, setGovPrograms] = useState<GovProgram[]>([]);
  const [aiPresets, setAiPresets] = useState<AIPreset[]>([]);
  const [taskCategories, setTaskCategories] = useState<TaskCategory[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  // 업무 등록 창에서 템플릿 이름을 고치면(TaskRegisterModal · 2026-10-26) 다시 받지 않고 그 이름만 바꿔 끼운다
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ id: string; name: string }>).detail;
      if (d && d.id) setTaskTemplates(prev => prev.map(t => (t.id === d.id ? { ...t, name: d.name } : t)));
    };
    // 업무 등록 창에서 단계를 템플릿에 반영 · 새 템플릿을 만들면(2026-10-29) 그 한 벌만 바꿔 끼우거나 더한다
    const onSaved = (e: Event) => {
      const t = (e as CustomEvent<TaskTemplate>).detail;
      if (t && t.id) setTaskTemplates(prev => (prev.some(x => x.id === t.id) ? prev.map(x => (x.id === t.id ? { ...x, ...t } : x)) : [...prev, t].sort((a, b) => a.name.localeCompare(b.name, 'ko'))));
    };
    window.addEventListener('tm:template-renamed', on);
    window.addEventListener('tm:template-saved', onSaved);
    return () => { window.removeEventListener('tm:template-renamed', on); window.removeEventListener('tm:template-saved', onSaved); };
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshCompanies = useCallback(async () => {
    try {
      const res = await api.getAllCompanies(); // R20260910: 500건 상한 제거 — 전체 페이지 이어받기
      setCompanies(res.data?.companies || []);
    } catch (e) { console.error('companies fetch:', e); }
  }, []);

  const refreshCompany = useCallback(async (id: string) => {
    try {
      const res = await api.getCompany(id);
      const row = res.data;
      if (!row) return;
      setCompanies((list) => (list.some((c) => c.id === id) ? list.map((c) => (c.id === id ? { ...c, ...row } : c)) : list.concat([row])));
    } catch (e) { console.error('company fetch:', e); }
  }, []);
  const saveCompany = useCallback(async (id: string, patch: Parameters<typeof api.updateCompany>[1]) => {
    await api.updateCompany(id, patch);   // 저장 알림(COMPANY_SAVED)으로 목록이 다시 읽힌다 — 여기서는 끝날 때까지 기다리게 한 번 더
    await refreshCompany(id);
  }, [refreshCompany]);
  // 어느 화면에서 업체를 저장하든 목록의 그 업체를 다시 읽음(2026-10-29 — 상담 결과에서 고른 업종 · 규모가 새로고침 전까지 안 보이던 것)
  useEffect(() => {
    const on = (e: Event) => { const id = (e as CustomEvent<{ id?: string }>).detail?.id; if (id) void refreshCompany(id); };
    window.addEventListener(api.COMPANY_SAVED, on);
    return () => window.removeEventListener(api.COMPANY_SAVED, on);
  }, [refreshCompany]);

  const refreshTasks = useCallback(async () => {
    try {
      const res = await api.getTasks();
      setTasks(res.data || []);
    } catch (e) { console.error('tasks fetch:', e); }
  }, []);

  const refreshFps = useCallback(async () => {
    try {
      const res = await api.getFps();
      setFps(res.data || []);
    } catch (e) { console.error('fps fetch:', e); }
  }, []);

  const refreshConsultations = useCallback(async () => {
    try {
      const res = await api.getConsultations();
      setConsultations(res.data || []);
    } catch (e) { console.error('consultations fetch:', e); }
  }, []);

  const refreshGov = useCallback(async () => {
    try {
      const res = await api.getGovPrograms();
      setGovPrograms(res.data || []);
    } catch (e) { console.error('gov fetch:', e); }
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [cRes, fRes, wRes, tRes, conRes, govRes, aiRes, catRes, tplRes] = await Promise.all([
        api.getAllCompanies(), // R20260910: 500건 상한 제거
        api.getFps(),
        api.getWms(),
        api.getTasks(),
        api.getConsultations(),
        api.getGovPrograms(),
        api.getAiPresets(),
        api.getTaskCategories(),
        api.getTaskTemplates(),
      ]);
      setCompanies(cRes.data?.companies || []);
      setFps(fRes.data || []);
      setWms(wRes.data || []);
      setTasks(tRes.data || []);
      setConsultations(conRes.data || []);
      setGovPrograms(govRes.data || []);
      setAiPresets(aiRes.data || []);
      setTaskCategories(catRes.data || []);
      setTaskTemplates(tplRes.data || []);
    } catch (e: unknown) {
      // AUTH_FIX_V1: 인증/세션 에러여도 강제 로그아웃하지 않음
      // (다중 API 동시 호출 중 단일 401로 전체 세션 종료되는 문제 방지)
      const msg = e instanceof Error ? e.message : '데이터 로딩 실패';
      setError(msg);
      console.error('TM refresh error:', e);
    } finally {
      setLoading(false);
    }
  }, []);
  // 화면으로 돌아오면 다시 읽기 — 다른 기기 · 다른 탭에서 등록한 업체 · 업무 · 상담이 바로 보이게
  //  (2026-10-29 지도사 「PC 에서 업체 등록했는데 휴대폰에 안 떠 · 실시간 되어야지」 — 처음 열 때 한 번만 읽던 것)
  //  가벼운 것만(업체 · FP · 업무 · 상담 — 공고 3천여 건은 빼고) · 20초 안에 또 오면 건너뜀
  useEffect(() => {
    let last = Date.now();
    const back = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 20000) return;
      last = Date.now();
      void Promise.all([refreshCompanies(), refreshFps(), refreshTasks(), refreshConsultations()]);
    };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    return () => { document.removeEventListener('visibilitychange', back); window.removeEventListener('focus', back); };
  }, [refreshCompanies, refreshFps, refreshTasks, refreshConsultations]);

  // 업무 단계를 어디서 고치든 업무 목록 다시 읽기(api.TASKS_SAVED · 2026-10-29) — 잇달아 고치면 0.5초 뒤 한 번만
  useEffect(() => {
    let t = 0;
    const on = () => { window.clearTimeout(t); t = window.setTimeout(() => { void refreshTasks(); }, 500); };
    window.addEventListener(api.TASKS_SAVED, on);
    return () => { window.clearTimeout(t); window.removeEventListener(api.TASKS_SAVED, on); };
  }, [refreshTasks]);

  useEffect(() => { refresh(); }, [refresh]);

  return {
    companies, fps, wms, tasks, consultations,
    govPrograms, aiPresets, taskCategories, taskTemplates,
    loading, error, refresh, refreshCompanies, refreshCompany, saveCompany, refreshTasks, refreshConsultations, refreshFps, refreshGov,
  };
}

/** 오늘 날짜 YYYY-MM-DD */
export function today(): string {
  const d = new Date();
  d.setHours(d.getHours() + 9); // KST
  return d.toISOString().slice(0, 10);
}

/** D-day 계산 */
export function dDay(deadline: string | null): number | null {
  if (!deadline) return null;
  const _t=new Date(deadline);_t.setHours(0,0,0,0);const _n=new Date();_n.setHours(0,0,0,0);return Math.round((_t.getTime()-_n.getTime())/86400000);
}

/** 날짜 포맷 (V20_MASTER:12 — ISO T...Z 부분 제거) */
export function fmtDate(d: string | null): string {
  if (!d) return '';
  return d.slice(0, 10).replace(/(\d{4})-(\d{2})-(\d{2})/, '$1.$2.$3');
}
